import type { BdrLead, Cadencia } from '@/types'
import { getHorarioSugestao, getLeadMeetingDate } from '@/utils/bdrPanelUtils'

export type QueueFilaItem = {
  lead: BdrLead
  cad: Cadencia | null
  horario?: ReturnType<typeof getHorarioSugestao>
}

/** Agrupa leads da fila operacional por tipo de cadência/canal. */
export function processBdrQueueFila(
  tipo: string,
  queueLeads: BdrLead[],
  getCadencia: (leadId: string) => Cadencia | null,
): QueueFilaItem[] {
  const todayStr = new Date().toISOString().split('T')[0]

  return queueLeads
    .map((lead) => {
      const cad = lead.cadencia ?? getCadencia(lead.id) ?? null
      return { lead, cad, horario: getHorarioSugestao(cad) }
    })
    .filter(({ lead, cad }) => {
      const statusNormalized = (lead.status || '').toUpperCase()
      if (statusNormalized === 'PERDIDO') return false
      if (!cad) {
        if (statusNormalized === 'NOVO' && tipo === 'planejamento') return true
        if (statusNormalized !== 'CONVERTIDO') return false
      }

      let rawProximaAcao = ''
      if (cad?.proximaAcao) {
        rawProximaAcao =
          typeof cad.proximaAcao === 'object'
            ? (cad.proximaAcao as Date).toISOString()
            : (cad.proximaAcao as string)
      } else if (tipo === 'reunioes') {
        rawProximaAcao = getLeadMeetingDate(lead)
      }

      const proximaAcaoStr = (rawProximaAcao || '').split('T')[0]
      const isHojeOuAtrasado = !proximaAcaoStr || proximaAcaoStr <= todayStr
      const isPlanejamento = proximaAcaoStr > todayStr

      const calcularCanalSugerido = (tent: number) => {
        if (tent <= 3) return 'WhatsApp'
        if (tent === 4) return 'Email'
        if (tent === 5) return 'Breakup'
        return 'Multi-canal'
      }
      const tentativaAtual = cad ? cad.tentativa : 0
      const nextCanal = cad ? calcularCanalSugerido(tentativaAtual + 1) : 'Multi-canal'

      const origem = lead.origem || ''
      const isSiteLead =
        origem.includes('Site') || origem.includes('Formulário') || origem.includes('Landing Page')

      if (statusNormalized === 'NOVO' && tentativaAtual === 0 && isSiteLead) {
        return tipo === 'recebidos'
      }

      if (statusNormalized === 'NOVO' && tentativaAtual === 0 && !isSiteLead) {
        return tipo === 'planejamento'
      }

      const statusCad = (cad?.statusCadencia || '').toUpperCase()
      const isFromStatus =
        statusCad.includes('HANDOFF') ||
        statusNormalized === 'SQL' ||
        statusNormalized.includes('REUNIÃO') ||
        statusNormalized === 'CONVERTIDO'

      if (isFromStatus && proximaAcaoStr) {
        const dateObj = new Date(rawProximaAcao)
        const todayObj = new Date()
        const isMonth =
          dateObj.getMonth() === todayObj.getMonth() &&
          dateObj.getFullYear() === todayObj.getFullYear()
        if (isMonth) {
          return tipo === 'reunioes'
        }
      }

      const hasDeal = lead.deals && lead.deals.length > 0
      if (hasDeal) return false
      if (statusNormalized === 'CONVERTIDO') return false

      if (!isHojeOuAtrasado || cad?.statusCadencia === 'SQL - Handoff') {
        if (isPlanejamento && cad?.statusCadencia !== 'SQL - Handoff') {
          return tipo === 'planejamento'
        }
        return false
      }

      if (['WhatsApp', 'Telefone', 'Multi-canal'].includes(nextCanal)) {
        return tipo === 'ligacoes'
      }

      if (['Email', 'Breakup'].includes(nextCanal)) {
        return tipo === 'followup'
      }

      return false
    })
    .sort((a, b) => {
      const prioridade: Record<number, number> = { 3: 1, 4: 2, 2: 3, 1: 4, 0: 5 }
      const pesoA = !a.cad ? 1 : a.cad.fase >= 3 || a.cad.tentativa >= 5 ? 2 : 1
      const pesoB = !b.cad ? 1 : b.cad.fase >= 3 || b.cad.tentativa >= 5 ? 2 : 1
      if (pesoA !== pesoB) return pesoA - pesoB

      const faseA = a.cad ? a.cad.fase : 0
      const faseB = b.cad ? b.cad.fase : 0
      return (prioridade[faseA] || 5) - (prioridade[faseB] || 5)
    })
}
